import { useTheme } from "arkh";
import { useMemo, useCallback } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { useToggleFontColor_page$key } from "./__generated__/useToggleFontColor_page.graphql";
import { useToggleFontColorSetTextColorMutation } from "./__generated__/useToggleFontColorSetTextColorMutation.graphql";

export function useToggleFontColor(props: {
  page: useToggleFontColor_page$key;
  noteId: string;
}) {
  const page = useFragment(
    graphql`
      fragment useToggleFontColor_page on WidgetNotePage {
        id
        fontColor
        ...useNoteWidgetStyleText_widgetNotePage
      }
    `,
    props.page
  );
  const [mutate] = useMutation<useToggleFontColorSetTextColorMutation>(graphql`
    mutation useToggleFontColorSetTextColorMutation(
      $noteId: ID!
      $pageId: ID!
      $value: WidgetNoteStyleInput!
    ) @raw_response_type {
      note(id: $noteId) {
        page(id: $pageId) {
          setStyle(style: $value) {
            id
            fontColor
          }
        }
      }
    }
  `);

  const theme = useTheme();
  const colors = useMemo(
    () => [theme.lightText, theme["text-primary"]],
    [theme]
  );
  const toggleColor = useCallback(() => {
    const currentIndex = colors.indexOf(page.fontColor ?? theme.lightText);
    const nextIndex =
      currentIndex === -1 ? 1 : (currentIndex + 1) % colors.length;
    mutate({
      optimisticResponse: {
        note: {
          page: {
            setStyle: { id: page.id, fontColor: colors[nextIndex] },
          },
        },
      },
      variables: {
        noteId: props.noteId,
        pageId: page.id,
        value: {
          fontColor: colors[nextIndex],
        },
      },
    });
  }, [colors, mutate, page.fontColor, page.id, props.noteId, theme.lightText]);

  return toggleColor;
}
